import React, { Fragment } from 'react';
import parse, { attributesToProps } from 'html-react-parser';
import PropTypes from 'prop-types';
import SubHead from '../SubHead/SubHead';
import PullQuote from '../PullQuote/PullQuote';
import AudienceHighlightsAndSocial from '../../../Page/AboutUs/AudienceHighlightsAndSocial';
import OurContent from '../../../Page/AboutUs/OurContent';
import Button from '../../../common/Button';
import './body-text.scss';
import { AFFILIATE_URLS } from '../../../../consts';
import { isValid, isText, decodeTags } from '../../../../utils';

const BodyText = ({ data, articleLayout, isAffiliate, className, isMobile, isPage, slug, editorsExpertsClass, aboutUsSocial, aboutUsHighlights, aboutUsContent, hasBuyButton = false }) => {
  if (!isValid(data)) { return null; }
  let encodedData = '';
  let renderedButton = null;
  let subHeader = null;
  let relatedStartIndex;
  let relatedEndIndex;
  let relatedLinkBodyText;
  let bodyTextAfterRelatedLink;
  let justBodyText;
  const isGuide = (articleLayout === 'Guide');
  const isContentHub = (articleLayout === 'Content Hub');
  const isStandard = (articleLayout === 'Standard');

  const isExpertsOrTeamPage = (isPage && isValid(slug)) && (slug === 'experts' || slug.includes('purewow-team') || slug === 'about-us');
  className = (isExpertsOrTeamPage && isValid(editorsExpertsClass)) ? className + ` ${editorsExpertsClass}` : className;
  className = (isExpertsOrTeamPage && !isValid(editorsExpertsClass)) ? className + ' page-reg-h2' : className;

  if (typeof data === 'object') {
    // standalone BodyText module
    encodedData = decodeTags(data.bodyText);
    className = (isValid(data.dropCase) && data.dropCase === true) ? `${className} drop-case-text` : `${className}`;
    subHeader = isValid(data.subHeader) ? data.subHeader : null;
    const { buttonText, buttonUrl } = data;
    const button = (isText(buttonText) && isText(buttonUrl)) ? { text: buttonText, url: buttonUrl } : null;
    renderedButton = button ? <Button button={button} /> : null;
  } else if (typeof data === 'string') {
    // BodyText module that's a child of another module like ImageModule (in which case it's just the rich text editor field)
    encodedData = decodeTags(data);
  }

  // check if isExpertsPage and encodedData has h2, add experts class to className
  const h2BlockHeading = '<h2 class="wp-block-heading">';
  const hasH2 = isText(encodedData) ? encodedData.includes(h2BlockHeading) : null;
  if (isText(encodedData) && isExpertsOrTeamPage && hasH2) {
    const ogH2Tag = new RegExp(h2BlockHeading, 'g');
    const newClassTag = h2BlockHeading.slice(0, -2) + className + h2BlockHeading.slice(-2);
    encodedData = encodedData.replace(ogH2Tag, newClassTag);
    const closingH2Tag = '</h2>';
    const ogClosingH2Tag = new RegExp(closingH2Tag, 'g');
    const hrElement = '<hr class="h2-hr">';
    const newClosingH2Tag = `${ogClosingH2Tag}${hrElement}`;
    encodedData = encodedData.replace('</h2>', newClosingH2Tag);
  }

  const pullQuoteTags = [
    "<figure class='wp-block-pullquote'>",
    '<figure class="wp-block-pullquote">',
    '<figure class=wp-block-pullquote>',
  ];
  const hasPullQuote = isText(encodedData) ? pullQuoteTags.some(tag => encodedData.includes(tag)) : null;
  if (isText(encodedData) && hasPullQuote) {
    pullQuoteTags.forEach((tag) => {
      const pqTag = tag;
      const ogPqTag = new RegExp(pqTag, 'g');
      const quotationMark = "<img class='quotation-mark' src='https://www.purewow.com/static-gmg/purewow/icons/quote+mark.webp' alt='quotation mark' loading='lazy' />";
      const pqTagWithQuotationElement = pqTag + quotationMark;
      encodedData = encodedData.replace(ogPqTag, pqTagWithQuotationElement);
      const wrappingTag = '<blockquote><p>';
      const ogTag = new RegExp(wrappingTag, 'g');
      const newClassTag = wrappingTag.slice(0, 14) + " class='pq-body'" + wrappingTag.slice(-2);
      encodedData = encodedData.replace(ogTag, newClassTag);
    });
  }

  // array of image embed tag possibilities (single vs double quotes are important here bc the articleLayout is affecting where single vs dq placement is happening - weird, i know)
  const imageEmbedTags = [
    "<figure class='wp-block-image size-full'>",
    '<figure class="wp-block-image size-full">',
    '<figure class="wp-block-image size-large">',
    '<div class="wp-block-image"><figure class="aligncenter size-full is-resized">'];
  // check if body-text module contains any embedded images
  const hasImageEmbed = isText(encodedData) ? imageEmbedTags.some(tag => encodedData.includes(tag)) : null;
  /* Check articleLayout and amend imageEmbedTag classname if a Guide or Content Hub:
    - loop over imageEmbedTags, for each tag, check if it appears inside encodedData && if articleLayout is Guide || CH
    - if true, add guideContentHubClassName to the tag
    - declare newClass variable and assign it the value of str.slice(0, -2) + " guide-content-hub" + str.slice(-2);
  */
  
  if (isText(encodedData) && hasImageEmbed) {
    imageEmbedTags.forEach((tag) => {
      if (isGuide || isContentHub) {
        let ogTag = new RegExp(tag, 'g');
        let newClassTag = tag.slice(0, -2) + " guide-content-hub" + tag.slice(-2);
        encodedData = encodedData.replace(ogTag, newClassTag);
      }
      if (isPage) {
        let ogTag = new RegExp(tag, 'g');
        let newClassTag = tag.slice(0, -2) + " page-image" + tag.slice(-2);
        encodedData = encodedData.replace(ogTag, newClassTag);
      }
    });
  }

  const freeformBtnTag = "<div class='wp-block-button'>";
  const freeformBtnTagOther = '<div class="wp-block-button">';
  const freeformBtnLinkTag = 'wp-block-button__link';
  const hasFreeformBtnLinkTag = (isText(encodedData) && encodedData.includes(freeformBtnLinkTag));
  const hasFreeformBtn = (isText(encodedData) && (encodedData.includes(freeformBtnTag) || encodedData.includes(freeformBtnTagOther)));
  const freeformBtnContainerClass = isStandard ? 'wp-block-button-container standard' : 'wp-block-button-container';
  const freeformBtnStrikethroughTag = '<s>';

  // Remove those empty FF divs with className 'wp-block-buttons' (they're empty!)
  const emptyFFDivTag = '<div class="wp-block-buttons"></div>';
  const hasEmptyFFDiv = (isText(encodedData) && encodedData.includes(emptyFFDivTag));
  if (isText(encodedData) && hasEmptyFFDiv) {
    const emptyFFDivRegEx = new RegExp(emptyFFDivTag, 'g');
    encodedData = encodedData.replace(emptyFFDivRegEx, '');
  }
  const emptyTagArray = ['<h3></h3>', '<h4></h4>', '<p></p>'];
  emptyTagArray.forEach((string) => {
    if (isText(encodedData) && encodedData.includes(string)) {
      encodedData = encodedData.replace(string, '');
    }
  });
  
  // wrap FF buttons in a wrapper div (so that for shoppable articles where there are two buttons sometimes, they can be styled in a row, instead of being stacked)
  // i want to isolate the button divs in their own variable and insert them into this wrapper div so can style it as a row.
  if (isText(encodedData) && hasFreeformBtn) {
    // find index where freeform button(s) begin
    let ffButtonStartIndex = encodedData.indexOf(freeformBtnTag);
    if (ffButtonStartIndex < 0) {
      ffButtonStartIndex = encodedData.indexOf(freeformBtnTagOther);
    } 

    // save the freeform button(s) tag and contents in a variable
    const ffButtonText = encodedData.slice(ffButtonStartIndex);
    // insert freeform button(s) into new wrapper div
    let newlyWrappedButtons = `<div class="${freeformBtnContainerClass}">${ffButtonText}</div>`;
    // if Standard article layout, replace button classname with updatedBtnTag
    const ogFFTag = new RegExp(freeformBtnTag, 'g');
    const ogFFTagOther = new RegExp(freeformBtnTagOther, 'g');
    const updatedBtnTag = freeformBtnTag.replace('wp-block-button', 'wp-block-button standard');
    newlyWrappedButtons = isStandard ? newlyWrappedButtons.replace(ogFFTag, updatedBtnTag).replace(ogFFTagOther, updatedBtnTag) : newlyWrappedButtons;
    justBodyText = encodedData.slice(0, ffButtonStartIndex);
    encodedData = justBodyText + newlyWrappedButtons;
  }
  // check if article isAffiliate and if body-text contains freeform button, then add a class of affiliate-btn to the freeform button bc styling is the pink button on Affiliate articles
  if (isText(encodedData) && isAffiliate && hasFreeformBtn) {
    let ogTag = new RegExp(freeformBtnTag, 'g');
    let ogTagOther = new RegExp(freeformBtnTagOther, 'g');
    let newClassTag = freeformBtnTag.replace('wp-block-button', 'affiliate-btn');
    encodedData = encodedData.replace(ogTag, newClassTag);
    encodedData = encodedData.replace(ogTagOther, newClassTag);
    // additional logic when button tag includes standard class
    let standardTag = new RegExp("<div class='wp-block-button standard'>", 'g');
    let standardTagOther = new RegExp('<div class="wp-block-button standard">', 'g');
    if (hasBuyButton) {
      encodedData = encodedData.replace(standardTag, "<div class='affiliate-btn standard' data-link-type='buy-button'>").replace(standardTagOther, "<div class='affiliate-btn standard'>");
    } else {
      encodedData = encodedData.replace(standardTag, "<div class='affiliate-btn standard'>").replace(standardTagOther, "<div class='affiliate-btn standard'>");
    }
  }
  if (isText(encodedData) && isAffiliate && hasFreeformBtnLinkTag) {
    let ogTag = new RegExp(freeformBtnLinkTag, 'g');
    let newClassTag = freeformBtnLinkTag.replace('wp-block-button__link', 'affiliate-btn__link');
    encodedData = encodedData.replace(ogTag, newClassTag);
    if (encodedData.includes('<s>') && newClassTag === 'affiliate-btn__link') {
      encodedData = encodedData.replace(/<s>/g, '<s style="font-weight:200;">');
    }
    if (encodedData.includes('|') && newClassTag === 'affiliate-btn__link') {
      encodedData = encodedData.replace(/\|/g, '<span style="font-weight:200;">|</span>');
    }
    if (AFFILIATE_URLS.some(el => encodedData.includes(el))) {
      encodedData = encodedData.replace("noopener nofollow", "noopener sponsored noskim");
    }
  }

  const relatedTag = '<p class="related-story-link">';
  const renderedRelatedTag = '<div class="related-story-link-container">';
  const hasRelatedStoryLink = (isText(encodedData) && encodedData.includes(relatedTag) && !encodedData.includes(renderedRelatedTag));
  const strongTag = '<strong>RELATED</strong>: ';
  // if encodedData includes relatedTag, save the body text and the related story link into their own variables to render:
  if (isText(encodedData) && hasRelatedStoryLink) {
    // find index where relatedTag begins
    relatedStartIndex = encodedData.indexOf(relatedTag);
    // save the related tag and its contents in a variable
    relatedLinkBodyText = encodedData.slice(relatedStartIndex);
    // find the index where the closing p tag of the related story link ends
    relatedEndIndex = relatedLinkBodyText.indexOf('</p>');
    relatedEndIndex += 4; // so that it includes up to the closing '>' of '</p>'
    // save the body text that was getting included within the related link container into its own variable
    bodyTextAfterRelatedLink = relatedLinkBodyText.slice(relatedEndIndex);
    relatedLinkBodyText = relatedLinkBodyText.substring(0, relatedEndIndex);
    if (relatedLinkBodyText.includes('&nbsp;')) {
      relatedLinkBodyText = relatedLinkBodyText.replace(/&nbsp;/g, ' ');
    }
    if (relatedLinkBodyText.includes(strongTag)) {
      relatedLinkBodyText = relatedLinkBodyText.replace(strongTag, '');
    }
    const included = relatedLinkBodyText.toLowerCase().includes('related: ');
    if (included) {
      relatedLinkBodyText = relatedLinkBodyText.toLowerCase().replace('related: ', '');
    }
    // save the bodyText minus relatedLinkBodyText text in a new variable
    justBodyText = encodedData.slice(0, relatedStartIndex);

  }

  const hasSubHeader = isText(subHeader);

  return (
    <Fragment>
      {hasRelatedStoryLink ? (
        <Fragment>
          {hasSubHeader && <SubHead className={isStandard ? 'sub-head standard' : 'sub-head'} data={subHeader} />}
          <div className={`${className} ${isStandard ? 'standard' : ''}`} dangerouslySetInnerHTML={ { __html: justBodyText } } />
          <div className="related-story-link-container">
            <div className="related-header">
              <strong>RELATED</strong>
              <hr></hr>
            </div>
            <div className={className} dangerouslySetInnerHTML={ { __html: relatedLinkBodyText } } />
            <hr></hr>
          </div>
          {(isValid(bodyTextAfterRelatedLink))
            && <div className='body-text' dangerouslySetInnerHTML={ { __html: bodyTextAfterRelatedLink } } />
          }
        </Fragment>
      ) : (
        <Fragment>
        {subHeader && <SubHead className={isStandard ? 'sub-head standard' : 'sub-head'} data={subHeader} isPage={isPage} slug={slug} editorsExpertsClass={editorsExpertsClass} />}
        {isText(encodedData) && <div className={`${className} ${isStandard ? 'standard' : ''}`} dangerouslySetInnerHTML={ { __html: encodedData } } />}
        {aboutUsSocial && <AudienceHighlightsAndSocial isSocial={true} hedUrl={slug} />}
        {aboutUsContent && <OurContent hedUrl={slug} />}
        {aboutUsHighlights && <AudienceHighlightsAndSocial isHighlights={true} hedUrl={slug} />}
        </Fragment>
      )}
      {renderedButton}

    </Fragment>
  );
};

BodyText.propTypes = {
  className: PropTypes.string,
};

export default BodyText;
